export const contactsTableColumns = [
  { id: "name", label: "Name" },
  { id: "email", label: "E-mail", minWidth: 180 },
  { id: "radarMonitoring", label: "Radar monitoring", minWidth: 160 },
  { id: "actions", label: "Actions", align: "right" as const },
] as const;

import { format, parseISO } from "date-fns";
import type { ContactDetailResponseDto } from "@shared/generated/crm/models/ContactDetailResponseDto";
import type { ContactBriefing } from "./contacts.types";
import { GetContactQueryResponse } from "@shared/generated/crm/models/contactsController/GetContact";

function normalizeFullName(fullName: string | null | undefined): string {
  if (!fullName) return "";
  const commaIndex = fullName.indexOf(",");
  if (commaIndex === -1) return fullName;
  const last = fullName.slice(0, commaIndex).trim();
  const first = fullName.slice(commaIndex + 1).trim();
  return [first, last].filter(Boolean).join(" ");
}

export function deriveContactName(
  contact: Pick<
    ContactDetailResponseDto,
    "fullName" | "firstName" | "lastName" | "preferredName"
  >,
): string {
  const { firstName, lastName, preferredName, fullName } = contact;
  const normalizedFullName = normalizeFullName(fullName);

  if (firstName) {
    if (lastName) {
      return [firstName, preferredName ? `"${preferredName}"` : null, lastName]
        .filter(Boolean)
        .join(" ");
    }
    if (preferredName) {
      return [firstName, `"${preferredName}"`].join(" ");
    }
    return normalizedFullName || firstName;
  }

  return normalizedFullName || lastName || "Unknown";
}

export function deriveLatestPendingFieldSignalAt(
  signals: { occurredAt: string }[],
): string | undefined {
  return signals
    .map((s) => s.occurredAt)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
}

export function toBriefing(contact: GetContactQueryResponse): ContactBriefing {
  const name = deriveContactName(contact);

  const titleAndCompany = [contact.title, contact.company]
    .filter(Boolean)
    .join(" @ ");

  const location = [contact.city, contact.state].filter(Boolean).join(", ");

  const crmStatus = `Added to CRM on ${format(parseISO(contact.createdAt), "MMM d, yyyy")}`;

  return {
    contact,
    header: {
      id: contact.id,
      name,
      titleAndCompany,
      location,
      crmStatus,
    },
    recentActivity: [],
  };
}
